import React from "react";
import { TFarmDataResponse } from "../services/farm.service";
import SpeedoMeter from "../pages/sensors/component/SpeedoMeter";
import { useRecoilValue } from "recoil";
import { languageAtom } from "../state/atoms/language.atom";
import { Insights, Sensors, TestIds } from "../pages/plot";
import { t } from "../services/locale.service";

const SpeedoMetersContainer: React.FC<TProps> = (props) => {
  const { farmData, plotId, orgId, speedoMeterFor } = props;
  const SpeedoMeterSize = Math.min(getWindowDimensions().width / 2.8, 150);

  const selectedLanguage = useRecoilValue(languageAtom);
  const testId = TestIds.speedoMeterContainer.replace(
    ":speedoMeterFor",
    speedoMeterFor!
  );

  const getSpeedoMeterData = (sensor: string) => {
    const highlights = farmData[sensor].labels?.map((label) => ({
      from: label.min,
      to: label.max,
      color: label.activeBarColor,
    }));

    const value = Number(Number(farmData[sensor]?.value).toFixed(2));
    const majorTicks = [
      (farmData[sensor].minValue ?? 0).toString(),
      (farmData[sensor].maxValue ?? 100).toString(),
    ];

    const activeLabel = farmData[sensor].labels?.filter((label) => {
      return label.min <= value && label.max >= value;
    })[0];

    const description = activeLabel?.name;
    const descriptionBackgroundColor = activeLabel?.activeBarColor;

    return {
      highlights,
      value,
      majorTicks,
      activeLabel,
      description,
      descriptionBackgroundColor,
    };
  };

  if (Object.keys(farmData).length === 0) {
    return null;
  }

  return (
    <div style={ContainerStyle}>
      <div data-testid={testId} style={SpeedoMetersContainerStyle}>
        {Object.keys(farmData).map((sensor, index) => {
          const SpeedoMeterSensors =
            speedoMeterFor === "sensor" ? Sensors : Insights;

          const isDisease = ["disease", "pest"].includes(speedoMeterFor!);
          if ((SpeedoMeterSensors.includes(sensor) || isDisease) && farmData[sensor]?.value) {
            const {
              highlights,
              value,
              majorTicks,
              description,
              descriptionBackgroundColor,
            } = getSpeedoMeterData(sensor);

            /**
             * TODO: sensor name color se aa raha
             */
            const sensorName = isDisease
              ? farmData[sensor].color
              : speedoMeterFor === "sensor"
                ? t(sensor)
                : t(sensor);

            const unit = isDisease
              ? "%"
              : speedoMeterFor === "sensor"
                ? SensorUnits[sensor]
                : "";

            // Dynamically assign pestId or diseaseId based on farmData
            const pestId =
              speedoMeterFor === "pest" ? (farmData[sensor] as { pestId?: string })?.pestId : undefined;
            const diseaseId =
              speedoMeterFor === "disease"
                ? (farmData[sensor] as { diseaseId?: string })?.diseaseId
                : undefined;

            return (
              <div
                style={SpeedometerWrapperStyle}
                key={`speedo-meter-${plotId}-${sensor}-${index}`}
              >
                <SpeedoMeter
                  key={`speedo-meter-${plotId}-${sensor}-${index}`}
                  plotId={plotId}
                  orgId={orgId}
                  sensor={sensor}
                  highlights={highlights}
                  value={value}
                  majorTicks={majorTicks}
                  valueStatus={description}
                  valueStatusColor={descriptionBackgroundColor}
                  speedoMeterSize={SpeedoMeterSize}
                  sensorName={sensorName}
                  unit={unit}
                  pestId={pestId}
                  diseaseId={diseaseId}
                  language={selectedLanguage ?? "en"}
                  isDisease={speedoMeterFor === "disease"}
                  isPest={speedoMeterFor === "pest"}
                  isApplicable={
                    ["pest", "disease"].includes(speedoMeterFor!)
                      ? farmData[sensor].isApplicable
                      : true
                  }
                />
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default SpeedoMetersContainer;

/**
 * ================== TYPES ======================
 */

type TSpeedoMeterFor = "sensor" | "disease" | "pest" | "insight";

type TProps = {
  farmData: TFarmDataResponse;
  plotId: string;
  orgId?: string;
  speedoMeterFor?: TSpeedoMeterFor;
};

/**
 * ================== Constants ======================
 */

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SensorKeyMapping: { [key: string]: string } = {
  airHumidity: "Air Humidity",
  airPressure: "Air Pressure",
  airTemp: "Air Temperature",
  leafWetness: "Leaf Wetness",
  lightIntensity: "Light Intensity",
  rainFall: "Rainfall",
  soilMoisture1: "Soil Moisture 1",
  soilMoisture2: "Soil Moisture 2",
  moisture1: "Moisture 1",
  moisture2: "Moisture 2",
  soilTemp: "Soil Temperature",
  windSpeed: "Wind Speed",
};

const SensorUnits: { [key: string]: string } = {
  soilMoisture1: "kPa",
  soilMoisture2: "kPa",
  moisture1: "%",
  moisture2: "%",
  airTemp: "℃",
  soilTemp: "℃",
  airHumidity: "%",
  rainFall: "mm",
  lightIntensity: "lux",
  airPressure: "kPa",
  windSpeed: "km/h",
};

// const InsightKeyMapping: { [key: string]: string } = {
//   deltaT: "Delta T",
//   VPD: "VPD",
// };

// styles
const ContainerStyle: React.CSSProperties = {
  display: "flex",
  width: "100%",
  flexDirection: "column",
};

const SpeedoMetersContainerStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  width: "100%",
  padding: "16px",
};

const SpeedometerWrapperStyle: React.CSSProperties = {
  flexBasis: "calc(48% - 10px)",
};

/**
 * ================== Helpers Functions ======================
 */

const getWindowDimensions = () => {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
};
